import React, { useState } from 'react';
import {
  Button,
  MenuOpenChangeData,
  MenuTrigger,
  Menu,
  MenuButton,
  MenuItem,
  MenuGroup,
  Body1Strong,
  MenuGroupHeader,
  MenuDivider,
} from '@fluentui/react-components';
import { reportMixPanel } from '@55degrees/consent-tracker';
import { DateFormats } from '@55degrees/layout-r18';
import SettingsIcon from '@mui/icons-material/Settings';
import { FormattedMessage, useIntl } from 'react-intl';
import CookieIcon from '@mui/icons-material/Cookie';
import { ProductFruitsReporter } from '../../../Helpers/ProductFruitsInit';
import { SettingsDropdownProps } from './type';
import { DateFormat, Locale } from '../../../../types/A3';
import SettingItemDropdown from './SettingItemDropdown';
import { ManageConsentButton, SettingsPopover } from './styling';
import useAppSettingsQuery from '../../../../hooks/useAppSettings';
import { StartupMode } from '../../../../types/AppSettings';
import {
  isValidWeekStartsOn,
  WEEK_STARTS_ON_OPTIONS,
  WeekStartsOnDay,
  weekStartsOnToMixpanelDay,
} from '../../../../helper';
import { HistoryDataFetchLimit, HISTORY_DATA_FETCH_LIMITS } from '../../../../config';

const weekStartsOnDayMessageIds: Record<WeekStartsOnDay, string> = {
  0: 'setting.weekStartsOn.day.sunday',
  1: 'setting.weekStartsOn.day.monday',
  2: 'setting.weekStartsOn.day.tuesday',
  3: 'setting.weekStartsOn.day.wednesday',
  4: 'setting.weekStartsOn.day.thursday',
  5: 'setting.weekStartsOn.day.friday',
  6: 'setting.weekStartsOn.day.saturday',
};

const weekStartsOnDayDefaults: Record<WeekStartsOnDay, string> = {
  0: 'Sunday',
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
};

const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

function SettingsDropdown({ isDarkMode, handleOpenConsent, setIsDarkMode }: SettingsDropdownProps) {
  const { formatMessage } = useIntl();

  const appSettingsQuery = useAppSettingsQuery();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  if (appSettingsQuery.isLoading || appSettingsQuery.isError) {
    if (appSettingsQuery.isError) {
      console.warn('Failed getting app settings');
    }

    return (
      <Button
        disabled
        appearance="subtle"
        icon={<SettingsIcon fontSize="medium" />}
        id="a3-settings"
        shape="circular"
        size="large"
      />
    );
  }

  const {
    data: { dateFormat, historicalDataFetchLimit, locale, startupMode, weekStartsOn },
  } = appSettingsQuery;

  const clearBrowserStorage = () => {
    window.localStorage.clear();
    window.location.reload();
  };

  const onDateFormatChange = (newValue: DateFormat) => {
    appSettingsQuery.updateDateFormat(newValue);
    reportMixPanel('Date Format Change', { 'Date Format': newValue });
  };

  const onWeekStartsOnChange = (newValue: string) => {
    const parsed = Number.parseInt(newValue, 10);
    if (!isValidWeekStartsOn(parsed)) return;
    appSettingsQuery.updateWeekStartsOn(parsed as WeekStartsOnDay);
    reportMixPanel('Week Start Change', {
      Day: weekStartsOnToMixpanelDay(parsed as WeekStartsOnDay),
    });
  };

  const onLanguageChange = (newValue: Locale) => {
    appSettingsQuery.updateLocale(newValue);
    reportMixPanel('Language Change', { Language: newValue });
  };

  const onThemeChange = (newValue: 'Dark' | 'Light') => {
    setIsDarkMode(newValue === 'Dark');
    reportMixPanel('Color Scheme Change', { 'Color Scheme': `${newValue}` });
  };

  const onStartupModeChange = (newValue: StartupMode) => {
    appSettingsQuery.updateAutoLoadType(newValue);
    const mixpanelValues: Record<StartupMode, string> = {
      'landing-page': 'Landing Page',
      example: 'Example Data',
      'last-loaded': 'Last Loaded',
    };
    reportMixPanel('Start Page Change', {
      'Start Page': mixpanelValues[newValue] || 'Landing Page',
    });
  };

  const onHistoricalDataLimitChange = (newValue: `${HistoryDataFetchLimit}`) => {
    const parsed = Number(newValue) as HistoryDataFetchLimit;
    if (!HISTORY_DATA_FETCH_LIMITS.includes(parsed)) return;
    appSettingsQuery.updateHistoryFetchLimit(parsed);
    reportMixPanel('Historical Data Fetch Limit', {
      'Fetch Limit': parsed,
    });
  };

  const onOpenChange = (_, { open }: MenuOpenChangeData) => {
    if (open) ProductFruitsReporter.track('openSettings');
    setIsMenuOpen(open);
  };

  const onConsentClick = () => {
    setIsMenuOpen(false);
    handleOpenConsent();
  };

  const autoloads = [
    {
      id: 'option-autoload-landing-page',
      text: formatMessage({
        id: 'setting.autoload.landingPage',
        defaultMessage: 'Landing Page',
        description: 'Autoload option',
      }),
      value: 'landing-page',
    },
    {
      id: 'option-autoload-last-loaded',
      text: formatMessage({
        id: 'setting.autoload.lastLoaded',
        defaultMessage: 'Last data Loaded',
        description: 'Autoload option',
      }),
      value: 'last-loaded',
    },
    {
      id: 'option-autoload-example',
      text: formatMessage({
        id: 'setting.autoload.example',
        defaultMessage: 'Example data',
        description: 'Autoload option',
      }),
      value: 'example',
    },
  ];

  const themes = [
    {
      id: 'option-theme-dark',
      text: formatMessage({
        id: 'setting.scheme.dark',
        defaultMessage: 'Dark',
        description: 'Theme option',
      }),
      value: 'Dark',
    },
    {
      id: 'option-theme-light',
      text: formatMessage({
        id: 'setting.scheme.light',
        defaultMessage: 'Light',
        description: 'Theme option',
      }),
      value: 'Light',
    },
  ];

  const languages = [
    {
      id: 'option-language-english',
      text: formatMessage({
        id: 'setting.language.english',
        defaultMessage: 'English',
        description: 'Language option',
      }),
      value: 'en-US',
    },
    {
      id: 'option-language-french',
      text: formatMessage({
        id: 'setting.language.french',
        defaultMessage: 'French',
        description: 'Language option',
      }),
      value: 'fr-FR',
    },
  ];

  const startupModeOptions = autoloads.map(({ id, text, value }) => ({
    key: id,
    label: text,
    value: value,
  }));

  const dateFormatOptions = DateFormats.map((date) => ({
    key: `option-dateformat-${date}`,
    label: date,
    value: date,
  }));

  const weekStartsOnOptions = WEEK_STARTS_ON_OPTIONS.map((day) => ({
    key: `option-week-starts-on-${day}`,
    label: formatMessage({
      id: weekStartsOnDayMessageIds[day],
      defaultMessage: weekStartsOnDayDefaults[day],
      description: 'Week starts on day option',
    }),
    value: String(day),
  }));

  const languageOptions = languages.map(({ id, text, value }) => ({
    key: id,
    label: capitalize(text),
    value: value,
  }));

  const themeOptions = themes.map(({ id, text, value }) => ({
    key: id,
    label: text,
    value: value,
  }));

  const historyDataFetchLimitOptions = HISTORY_DATA_FETCH_LIMITS.map((value) => ({
    key: `option-historical-data-limit-${value}`,
    label: String(value),
    value: String(value),
  }));

  return (
    <Menu open={isMenuOpen} onOpenChange={onOpenChange} persistOnItemClick>
      <MenuTrigger disableButtonEnhancement>
        <MenuButton
          appearance="subtle"
          icon={<SettingsIcon fontSize="medium" />}
          id="a3-settings"
          shape="circular"
          size="large"
        />
      </MenuTrigger>
      <SettingsPopover>
        <ManageConsentButton
          startIcon={<CookieIcon />}
          variant="text"
          size="small"
          onClick={onConsentClick}
        >
          <FormattedMessage
            id="setting.menu.item.manage.consent"
            defaultMessage="Manage Consent"
            description="Menu Item Button Text"
          />
        </ManageConsentButton>
        <MenuGroup>
          <MenuGroupHeader>
            <Body1Strong>
              <FormattedMessage
                id="setting.menu.item.cache.title"
                defaultMessage="Cache"
                description="Menu Group Text"
              />
            </Body1Strong>
          </MenuGroupHeader>
          <MenuItem onClick={clearBrowserStorage}>
            <FormattedMessage
              id="setting.menu.item.cache.clear"
              defaultMessage="Clear Browser Storage"
              description="Menu Item Button Text"
            />
          </MenuItem>
        </MenuGroup>
        <MenuDivider />
        <MenuGroup>
          <MenuGroupHeader>
            <Body1Strong>
              <FormattedMessage
                id="setting.menu.item.date.title"
                defaultMessage="Date Display"
                description="Menu Group Text"
              />
            </Body1Strong>
          </MenuGroupHeader>
          <SettingItemDropdown
            options={dateFormatOptions}
            onChange={onDateFormatChange}
            settingValue={dateFormat}
          />
        </MenuGroup>
        <MenuGroup>
          <MenuGroupHeader>
            <Body1Strong>
              <FormattedMessage
                id="setting.menu.item.weekStartsOn.title"
                defaultMessage="Week Starts On"
                description="Menu Group Text"
              />
            </Body1Strong>
          </MenuGroupHeader>
          <SettingItemDropdown
            options={weekStartsOnOptions}
            onChange={onWeekStartsOnChange}
            settingValue={String(weekStartsOn)}
          />
        </MenuGroup>
        <MenuGroup>
          <MenuGroupHeader>
            <Body1Strong>
              <FormattedMessage
                id="setting.menu.item.language.title"
                defaultMessage="Language"
                description="Menu Group Text"
              />
            </Body1Strong>
          </MenuGroupHeader>
          <SettingItemDropdown
            options={languageOptions}
            onChange={onLanguageChange}
            settingValue={locale}
          />
        </MenuGroup>
        <MenuGroup>
          <MenuGroupHeader>
            <Body1Strong>
              <FormattedMessage
                id="setting.menu.item.scheme.title"
                defaultMessage="Color Scheme"
                description="Menu Group Text"
              />
            </Body1Strong>
          </MenuGroupHeader>
          <SettingItemDropdown
            options={themeOptions}
            onChange={onThemeChange}
            settingValue={isDarkMode ? 'Dark' : 'Light'}
          />
        </MenuGroup>
        <MenuGroup>
          <MenuGroupHeader>
            <Body1Strong>
              <FormattedMessage
                id="setting.menu.item.autoload.title"
                defaultMessage="Start Page"
                description="Menu Group Text"
              />
            </Body1Strong>
          </MenuGroupHeader>
          <SettingItemDropdown
            options={startupModeOptions}
            onChange={onStartupModeChange}
            settingValue={startupMode}
          />
        </MenuGroup>
        <MenuGroup>
          <MenuGroupHeader>
            <Body1Strong>
              <FormattedMessage
                id="setting.menu.item.azureData.historicalFetchLimit.title"
                defaultMessage="Historical data fetch limit"
                description="Menu Group subtext"
              />
            </Body1Strong>
          </MenuGroupHeader>
          <SettingItemDropdown
            options={historyDataFetchLimitOptions}
            onChange={onHistoricalDataLimitChange}
            settingValue={String(historicalDataFetchLimit)}
          />
        </MenuGroup>
      </SettingsPopover>
    </Menu>
  );
}

export default SettingsDropdown;
